import { Close } from '@mui/icons-material';
import { Box, IconButton, Select, SelectChangeEvent, SelectProps } from '@mui/material';
import React, { useMemo, useState } from 'react';

export type ClearableSelectProps<T = unknown> = SelectProps<T> & {
    onClear?: () => void;
    disableClearable?: boolean;
};

export function ClearableSelect<T = unknown>(props: ClearableSelectProps<T>) {
    const { onClear, disabled, value, sx, onChange, children, disableClearable, ...selectProps } = props;
    const [isHovered, setIsHovered] = useState(false);

    const hasSelection = useMemo(() => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
    }, [value]);

    const canClear = !disabled && hasSelection && !disableClearable;

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!canClear) {
            return;
        }

        if (onClear) {
            onClear();
            return;
        }

        onChange?.(event as unknown as SelectChangeEvent<any>, '' as unknown as any);
    };

    return (
        <Box
            position='relative'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Select
                fullWidth
                {...selectProps}
                value={value}
                onChange={onChange}
                disabled={disabled}
                sx={{
                    ...sx,
                }}
            >
                {children}
            </Select>
            {canClear && (
                <IconButton
                    size='small'
                    onClick={handleClear}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{
                        position: 'absolute',
                        right: '30px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: (theme) => theme.palette.background.paper,
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: isHovered ? 'auto' : 'none',
                    }}
                    aria-label='clear selection'
                >
                    <Close fontSize='small' />
                </IconButton>
            )}
        </Box>
    );
};

